import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Landmark, Check } from "lucide-react";
import { ManualBankEntry } from "@/components/ManualBankEntry";

// Dual-Fuel funding selector: business debit card (instant delivery) vs
// Plaid-linked bank account (ACH pre-funding).
export const FundingSourceSelector = () => {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try { setHealth((await api.get("/funding/health")).data); } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const linkCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/funding/card-setup-intent");
      if (!window.Stripe || !data.publishable_key) {
        throw new Error("Stripe.js is unavailable — reload the page and try again.");
      }
      // Mount Stripe Elements: the card number is entered in Stripe's iframe
      // and never touches Checkmate's servers.
      const stripe = window.Stripe(data.publishable_key);
      const elements = stripe.elements();
      const card = elements.create("card", { hidePostalCode: false });
      setCardOpen(true);
      await new Promise((r) => setTimeout(r, 60));
      card.mount("#funding-card-element");
      cardRef.current = { stripe, card, clientSecret: data.client_secret };
    } catch (e) {
      toast.error(e?.message || formatApiError(e));
    }
    setBusy(false);
  };

  const confirmCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const { stripe, card, clientSecret } = cardRef.current;
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (error) throw new Error(error.message);
      await api.post("/funding/source", { mode: "instant_card", payment_method_id: setupIntent.payment_method });
      toast.success("Funding armed — instant payouts active");
      card.unmount();
      cardRef.current = null;
      setCardOpen(false);
      load();
    } catch (e) { toast.error(e?.message || formatApiError(e)); }
    setBusy(false);
  };

  const linkBank = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/funding/plaid/link-token");
      if (data.sandbox_stub) {
        toast.warning("Plaid keys not configured", { description: data.message });
        await api.post("/funding/plaid/exchange", {
          public_token: `sandbox-${Date.now()}`, institution_name: "Sandbox Bank", mask: "0000" });
        await api.post("/funding/source", { mode: "ach_bank" });
        toast.success("Sandbox bank linked — add Plaid keys to go live");
      } else if (window.Plaid) {
        await new Promise((resolve) => {
          window.Plaid.create({
            token: data.link_token,
            onSuccess: async (public_token, meta) => {
              await api.post("/funding/plaid/exchange", {
                public_token,
                institution_name: meta?.institution?.name,
                mask: meta?.accounts?.[0]?.mask });
              await api.post("/funding/source", { mode: "ach_bank" });
              toast.success("Bank account linked for ACH pre-funding");
              resolve();
            },
            onExit: () => resolve(),
          }).open();
        });
      } else {
        toast.error("Plaid Link script is not loaded");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const mode = health?.funding_mode;
  const Card = ({ id, active, icon: Icon, title, sub, bullets, cta, onClick }) => (
    <div data-testid={id} className={`hard-card p-5 flex-1 min-w-[280px] ${active ? "border-4 border-good" : ""}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" />
        <div className="font-heading font-black text-xl">{title}</div>
        {active && <span className="ml-auto border-2 border-good text-good px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1"><Check className="w-3 h-3" /> Active</span>}
      </div>
      <div className="font-mono text-[11px] text-muted-foreground mb-3">{sub}</div>
      <ul className="font-mono text-[11px] space-y-1 mb-4 list-disc pl-4">
        {bullets.map((b) => <li key={b}>{b}</li>)}
      </ul>
      <button onClick={onClick} disabled={busy} data-testid={`${id}-cta`} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
        {busy ? "Working…" : cta}
      </button>
    </div>
  );

  return (
    <div data-testid="funding-source-selector">
      <div className="overline mb-2">§ FUNDING SOURCE</div>
      <div className="font-mono text-xs text-muted-foreground mb-4 max-w-2xl leading-relaxed">
        Choose how payroll is funded and delivered. Only tokens are stored — card numbers and bank
        credentials never touch Checkmate's database.
      </div>
      <div className="flex flex-wrap gap-4">
        <Card
          id="funding-card-option" active={mode === "instant_card"} icon={Zap}
          title="Business Debit Card"          sub={health?.card_label ? `Linked: ${health.card_label}` : "Instant delivery rail"}
          bullets={["Arms the run for Instant Delivery",
                    "Employees with a debit-card destination are paid in minutes",
                    "1.5% convenience fee (min $0.50) per instant payout, shown at sign-off"]}
          cta={mode === "instant_card" ? "Relink Card" : "Link Card · Arm Instant"}
          onClick={linkCard}
        />
        <Card
          id="funding-bank-option" active={mode === "ach_bank"} icon={Landmark}
          title="Linked Bank Account (ACH)"
          sub={health?.bank_institution ? `Linked: ${health.bank_institution} ••••${health.bank_mask || ""}` : "Standard pre-funding rail"}
          bullets={["Plaid-verified business bank account",
                    "Standard transfers to employee bank accounts",
                    "No convenience fee · 48h clearing window"]}
          cta={mode === "ach_bank" ? "Relink Bank" : "Link Bank · Standard ACH"}
          onClick={linkBank}
        />
      </div>
      {cardOpen && (
        <div className="hard-card p-5 mt-4 max-w-md" data-testid="card-link-form">
          <div className="overline mb-2">§ STRIPE CARD LINK</div>
          <div id="funding-card-element" className="border-2 border-ink p-3 mb-3 bg-white" />
          <div className="font-mono text-[10px] text-muted-foreground mb-3">
            Entered inside Stripe's secure iframe — Checkmate never sees the card number.
          </div>
          <div className="flex gap-2">
            <button onClick={confirmCard} disabled={busy} data-testid="card-link-confirm" className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
              {busy ? "Saving…" : "Save Card · Arm Instant"}
            </button>
            <button onClick={() => { cardRef.current?.card?.unmount(); cardRef.current = null; setCardOpen(false); }} data-testid="card-link-cancel" className="border-2 border-ink px-4 py-2 font-mono uppercase text-[10px] tracking-widest">
              Cancel
            </button>
          </div>
        </div>
      )}
      {health && (
        <div className="mt-4" data-testid="funding-manual-bank">
          <div className="font-mono text-[11px] text-muted-foreground mb-2 leading-relaxed max-w-2xl">
            <b>Tier 3 · no Plaid?</b> Type the business account in and Stripe proves it with two micro-deposits.
            Once verified it behaves exactly like a Plaid-linked bank on the standard 48-hour ACH schedule.
            Your business debit card stays the primary instant rail throughout.
          </div>
          <ManualBankEntry
            scope="funding"
            state={{ status: health.bank_status, last4: health.bank_mask }}
            onChanged={load}
          />
        </div>
      )}
      {health && (
        <div className="mt-4 font-mono text-[11px]" data-testid="funding-selector-status">
          Current status: <b>{health.headline}</b>
          {health.plaid_configured === false && " · Plaid running in sandbox mode until keys are added"}
        </div>
      )}
    </div>
  );
};
